import { useMemo, useState, useEffect } from "react";
import { useGetFunctionalGraphService, PersonaItem, OutcomeItem, ScenarioItem, StepItem, ActionItem, ApiItem } from "@/service-hooks/use-get-functional-graph-service";
import { useParams } from "react-router-dom";
import useDebounce from "./use-debounce";
import type {
  FilterRow,
  FilterableLabel,
} from "@/components/elements/functional-graph-filter-panel";

// BREEZEAI-1054: split the applied filter list by node label and turn each
// label's rows into a `{ [attribute]: { [operator]: value } }` object that
// `useGetFunctionalGraphService` already knows how to serialise as
// `filters[key]` query params. Numeric attributes get coerced to Number so
// backend comparisons stay type-safe.
const NUMERIC_ATTRIBUTES = new Set<string>(["order"]);

// Escape regex metacharacters in a user-supplied value so we can safely
// interpolate it into a regex alternation. Skipping this would let a value
// like "a.b" match "acb", or "()" break the alternation entirely.
const escapeRegex = (s: string): string =>
  s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// BREEZEAI-1229: convert a single filter row into a regex fragment that
// matches the same set of strings the row's operator would. Used when
// multiple rows on the same attribute need to be OR-composed into one
// regex alternation. The result is un-anchored on both sides so the caller
// can wrap all fragments with `^(...)$` if desired — but the fragments
// themselves already carry their own anchoring (`^` for startsWith, `$` for
// endsWith, both for `$eq` / `$in`) so joining with `|` produces the
// correct union semantics on its own.
const rowToRegexFragment = (op: string, value: string): string | null => {
  const esc = escapeRegex(value);
  switch (op) {
    case "$containsi":
    case "$contains":
      return `.*${esc}.*`;
    case "$startsWithi":
    case "$startsWith":
      return `^${esc}.*`;
    case "$endsWithi":
    case "$endsWith":
      return `.*${esc}$`;
    case "$eq":
      return `^${esc}$`;
    case "$in": {
      // Comma-separated exact matches — each becomes its own `^v$`
      // alternative, joined in the outer alternation later.
      const parts = value
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean)
        .map((p) => `^${escapeRegex(p)}$`);
      return parts.length === 0 ? null : parts.join("|");
    }
    case "$regexi":
    case "$regex":
      // The value is already a regex; wrap in a non-capturing group so
      // outer alternation doesn't fight it (e.g. `a|b` under our own `|`).
      return `(?:${value})`;
    default:
      return null;
  }
};

// Any single case-insensitive operator in the group flips the coalesced
// regex to case-insensitive — matches user intent ("any case" on either
// row should apply to the combined match too).
const isCaseInsensitiveOp = (op: string): boolean =>
  op === "$containsi" ||
  op === "$startsWithi" ||
  op === "$endsWithi" ||
  op === "$regexi";

const filtersForLabel = (
  applied: FilterRow[] | undefined,
  label: FilterableLabel,
): Record<string, any> => {
  if (!applied || applied.length === 0) return {};

  // Group all rows for this label by attribute — every row on the same
  // attribute OR-composes with every other, regardless of operator. This
  // matches the QA-expected behaviour ("Name contains 'System' OR Name in
  // list 'User,Admin'" → returns personas matching *any* of those). Rows
  // on different attributes still AND-compose (via the outer serialiser)
  // which is the correct filter-across-fields semantic (e.g. "name X AND
  // description Y").
  const byAttribute: Record<string, FilterRow[]> = {};
  for (const row of applied) {
    if (row.label !== label) continue;
    if (!row.attribute) continue; // custom-property row with no key yet
    const raw = row.value?.trim();
    if (!raw) continue;
    (byAttribute[row.attribute] ??= []).push(row);
  }

  const out: Record<string, any> = {};
  for (const [attribute, rows] of Object.entries(byAttribute)) {
    if (rows.length === 1) {
      // Single row — no coalescing needed. Preserve the row's operator
      // as-is (including numeric coercion for numeric attributes) so we
      // don't unnecessarily convert `$eq 5` into `$regex ^5$`.
      const row = rows[0];
      const raw = row.value.trim();
      if (row.operator === "$in") {
        const parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
        out[attribute] = {
          $in: NUMERIC_ATTRIBUTES.has(attribute)
            ? parts.map((p) => Number(p))
            : parts,
        };
      } else {
        const value = NUMERIC_ATTRIBUTES.has(attribute) ? Number(raw) : raw;
        out[attribute] = { [row.operator]: value };
      }
      continue;
    }

    // Multi-row path. Numeric attributes fall back to $in-merged values —
    // regex on numbers doesn't map cleanly and we don't currently expose
    // range operators in the UI.
    if (NUMERIC_ATTRIBUTES.has(attribute)) {
      const nums: number[] = [];
      for (const r of rows) {
        const parts = r.value.split(",").map((p) => p.trim()).filter(Boolean);
        for (const p of parts) {
          const n = Number(p);
          if (Number.isFinite(n)) nums.push(n);
        }
      }
      out[attribute] = { $in: Array.from(new Set(nums)) };
      continue;
    }

    // String path — build a compound regex OR-ing every row.
    const fragments: string[] = [];
    let caseInsensitive = false;
    for (const r of rows) {
      const frag = rowToRegexFragment(r.operator, r.value.trim());
      if (!frag) continue;
      fragments.push(frag);
      if (isCaseInsensitiveOp(r.operator)) caseInsensitive = true;
    }
    if (fragments.length === 0) continue;
    const combined = fragments.length === 1
      ? fragments[0]
      : fragments.map((f) => `(?:${f})`).join("|");
    out[attribute] = caseInsensitive
      ? { $regexi: combined }
      : { $regex: combined };
  }
  return out;
};

export const useFunctionalGraphPagination = (
  appliedFilters?: FilterRow[],
) => {
  const { id = "" } = useParams();
  const [selectedPersona, setSelectedPersona] = useState<PersonaItem | null>(null);
  const [selectedOutcome, setSelectedOutcome] = useState<OutcomeItem | null>(null);
  const [selectedScenario, setSelectedScenario] = useState<ScenarioItem | null>(null);
  const [selectedStep, setSelectedStep] = useState<StepItem | null>(null);
  const [selectedAction, setSelectedAction] = useState<ActionItem | null>(null);
  const [outcomeSearch, setOutcomeSearch] = useState('');
  const [scenarioSearch, setScenarioSearch] = useState('');
  const [stepSearch, setStepSearch] = useState('');
  const [actionSearch, setActionSearch] = useState('');
  const [apiSearch, setApiSearch] = useState('');
  
  // Page states
  const [personaPage, setPersonaPage] = useState(1);
  const [outcomePage, setOutcomePage] = useState(1);
  const [scenarioPage, setScenarioPage] = useState(1);
  const [stepPage, setStepPage] = useState(1);
  const [actionPage, setActionPage] = useState(1);
  const [apiPage, setApiPage] = useState(1);

  const debouncedOutcomeSearch = useDebounce(outcomeSearch, 500);
  const debouncedScenarioSearch = useDebounce(scenarioSearch, 500);
  const debouncedStepSearch = useDebounce(stepSearch, 500);
  const debouncedActionSearch = useDebounce(actionSearch, 500);
  const debouncedApiSearch = useDebounce(apiSearch, 500);

  // BREEZEAI-1054: when the applied filter set changes, snap every per-label
  // page back to 1 — the filtered dataset is smaller than the unfiltered one,
  // so page N could be past the end and silently render as empty.
  const filterKey = useMemo(
    () =>
      (appliedFilters ?? [])
        .map((r) => `${r.label}|${r.attribute}|${r.operator}|${r.value}`)
        .join(";"),
    [appliedFilters],
  );
  useEffect(() => {
    setPersonaPage(1);
    setOutcomePage(1);
    setScenarioPage(1);
    setStepPage(1);
    setActionPage(1);
    setApiPage(1);
    // BREEZEAI-1054: when filters change, also drop any downstream selection
    // — otherwise Tasks/Scenarios/Steps/Actions keep rendering the previous
    // persona's children after a filter yields "0 personas".
    setSelectedPersona(null);
    setSelectedOutcome(null);
    setSelectedScenario(null);
    setSelectedStep(null);
    setSelectedAction(null);
  }, [filterKey]);

  // BREEZEAI-1054: split the applied filter rows into per-label objects so
  // each per-label query only sends predicates it can honour. Memoised on the
  // filter list so react-query cache keys don't churn per render.
  const personaFilters = useMemo(
    () => filtersForLabel(appliedFilters, "Persona"),
    [appliedFilters],
  );
  const outcomeFilters = useMemo(
    () => filtersForLabel(appliedFilters, "Outcome"),
    [appliedFilters],
  );
  const scenarioFilters = useMemo(
    () => filtersForLabel(appliedFilters, "Scenario"),
    [appliedFilters],
  );
  const stepFilters = useMemo(
    () => filtersForLabel(appliedFilters, "Step"),
    [appliedFilters],
  );
  const actionFilters = useMemo(
    () => filtersForLabel(appliedFilters, "Action"),
    [appliedFilters],
  );
  const apiFilters = useMemo(
    () => filtersForLabel(appliedFilters, "Api"),
    [appliedFilters],
  );

  // Persona query
  const personaQuery = useGetFunctionalGraphService({
    projectUuid: id,
    label: 'Persona',
    limit: 10,
    page: personaPage,
    filters:
      Object.keys(personaFilters).length > 0 ? personaFilters : undefined,
    enabled: !!id,
  });

  // Outcome query
  const outcomeQuery = useGetFunctionalGraphService({
    projectUuid: id,
    label: 'Outcome',
    limit: 10,
    page: outcomePage,
    filters: {
      ...(selectedPersona ? { personaId: selectedPersona.id } : {}),
      ...(debouncedOutcomeSearch ? { outcome: { '$containsi': debouncedOutcomeSearch } } : {}),
      ...outcomeFilters,
    },
    enabled: !!selectedPersona && !!id, // Only enable when a persona is selected
  });

  // Scenario query
  const scenarioQuery = useGetFunctionalGraphService({
    projectUuid: id,
    label: 'Scenario',
    limit: 10,
    page: scenarioPage,
    filters: {
        ...(selectedOutcome ? { outcomeId: selectedOutcome.id } : {}),
        ...(debouncedScenarioSearch ? { scenario: { '$containsi': debouncedScenarioSearch } } : {}),
        ...scenarioFilters,
      },
    enabled: !!selectedOutcome && !!id, // Only enable when an outcome is selected
  });

  // Step query
  const stepQuery = useGetFunctionalGraphService({
    projectUuid: id,
    label: 'Step',
    limit: 10,
    page: stepPage,
    filters: {
        ...(selectedScenario ? { scenarioId: selectedScenario.id } : {}),
        ...(debouncedStepSearch ? { step: { '$containsi': debouncedStepSearch } } : {}),
        ...stepFilters,
      },
    enabled: !!selectedScenario && !!id, // Only enable when a scenario is selected
  });

  // Action query
  const actionQuery = useGetFunctionalGraphService({
    projectUuid: id,
    label: 'Action',
    limit: 10,
    page: actionPage,
    filters: {
        ...(selectedStep ? { stepId: selectedStep.id } : {}),
        ...(debouncedActionSearch ? { action: { '$containsi': debouncedActionSearch } } : {}),
        ...actionFilters,
      },
    enabled: !!selectedStep && !!id, // Only enable when a step is selected
  });

  // Api query
  const apiQuery = useGetFunctionalGraphService({
    projectUuid: id,
    label: 'Api',
    limit: 10,
    page: apiPage,
    filters: {
        ...(selectedAction ? { actionId: selectedAction.id } : {}),
        ...(debouncedApiSearch ? { url: { '$containsi': debouncedApiSearch } } : {}),
        ...apiFilters,
      },
    enabled: !!selectedAction && !!id, // Only enable when an action is selected
  });

  // Handlers
  const handlePersonaClick = (persona: PersonaItem) => {
    setSelectedPersona(persona);
    setSelectedOutcome(null);
    setSelectedScenario(null);
    setSelectedStep(null);
    setSelectedAction(null);
    setOutcomePage(1); // Reset outcome page when selecting new persona
  };

  const handleOutcomeClick = (outcome: OutcomeItem) => {
    setSelectedOutcome(outcome);
    setSelectedScenario(null);
    setSelectedStep(null);
    setSelectedAction(null);
    setScenarioPage(1); // Reset scenario page when selecting new outcome
  };

  const handleScenarioClick = (scenario: ScenarioItem) => {
    setSelectedScenario(scenario);
    setSelectedStep(null);
    setSelectedAction(null);
    setStepPage(1); // Reset step page when selecting new scenario
  };

  const handleStepClick = (step: StepItem) => {
    setSelectedStep(step);
    setSelectedAction(null);
    setActionPage(1); // Reset action page when selecting new step
  };

  const handleActionClick = (action: ActionItem) => {
    setSelectedAction(action);
    setApiPage(1); // Reset api page when selecting new action
  };

  // Page navigation handlers
  const handlePersonaNextPage = () => setPersonaPage(prev => prev + 1);
  const handlePersonaPrevPage = () => setPersonaPage(prev => Math.max(1, prev - 1));
  
  const handleOutcomeNextPage = () => setOutcomePage(prev => prev + 1);
  const handleOutcomePrevPage = () => setOutcomePage(prev => Math.max(1, prev - 1));
  
  const handleScenarioNextPage = () => setScenarioPage(prev => prev + 1);
  const handleScenarioPrevPage = () => setScenarioPage(prev => Math.max(1, prev - 1));
  
  const handleStepNextPage = () => setStepPage(prev => prev + 1);
  const handleStepPrevPage = () => setStepPage(prev => Math.max(1, prev - 1));
  
  const handleActionNextPage = () => setActionPage(prev => prev + 1);
  const handleActionPrevPage = () => setActionPage(prev => Math.max(1, prev - 1));
  
  const handleApiNextPage = () => setApiPage(prev => prev + 1);
  const handleApiPrevPage = () => setApiPage(prev => Math.max(1, prev - 1));

  // Reset page to 1 when search terms change
  useEffect(() => {
    setOutcomePage(1);
  }, [debouncedOutcomeSearch]);

  useEffect(() => {
    setScenarioPage(1);
  }, [debouncedScenarioSearch]);

  useEffect(() => {
    setStepPage(1);
  }, [debouncedStepSearch]);

  useEffect(() => {
    setActionPage(1);
  }, [debouncedActionSearch]);

  useEffect(() => {
    setApiPage(1);
  }, [debouncedApiSearch]);

  // Get data from current page
  const personas = personaQuery.data?.data as PersonaItem[] || [];
  const outcomes = outcomeQuery.data?.data as OutcomeItem[] || [];
  const scenarios = scenarioQuery.data?.data as ScenarioItem[] || [];
  const steps = stepQuery.data?.data as StepItem[] || [];
  const actions = actionQuery.data?.data as ActionItem[] || [];
  const apis = apiQuery.data?.data as ApiItem[] || [];

  // BREEZEAI-1405: a column only loads once its parent is selected, and
  // applying filters clears every selection (see the filterKey effect) — so a
  // Task filter added on top of a Persona filter matched nothing visible until
  // the user re-clicked a persona. While filters are applied, select the first
  // match in each column down to the deepest filtered label, so the filtered
  // path is open. Each query's data is for its current key (no placeholder
  // data), so a level is only picked once its own filtered results are in.
  const deepestFilteredLevel = [
    personaFilters,
    outcomeFilters,
    scenarioFilters,
    stepFilters,
    actionFilters,
    apiFilters,
  ].reduce(
    (deepest, f, level) => (Object.keys(f).length > 0 ? level : deepest),
    -1,
  );
  useEffect(() => {
    if (deepestFilteredLevel < 0) return;
    if (!selectedPersona) {
      if (personas[0]) handlePersonaClick(personas[0]);
      return;
    }
    if (deepestFilteredLevel < 1) return;
    if (!selectedOutcome) {
      if (outcomes[0]) handleOutcomeClick(outcomes[0]);
      return;
    }
    if (deepestFilteredLevel < 2) return;
    if (!selectedScenario) {
      if (scenarios[0]) handleScenarioClick(scenarios[0]);
      return;
    }
    if (deepestFilteredLevel < 3) return;
    if (!selectedStep) {
      if (steps[0]) handleStepClick(steps[0]);
      return;
    }
    if (deepestFilteredLevel < 4) return;
    if (!selectedAction && actions[0]) handleActionClick(actions[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deepestFilteredLevel,
    personaQuery.data,
    outcomeQuery.data,
    scenarioQuery.data,
    stepQuery.data,
    actionQuery.data,
    selectedPersona,
    selectedOutcome,
    selectedScenario,
    selectedStep,
    selectedAction,
  ]);

  // Get total counts
  const personasTotal = personaQuery.data?.total || 0;
  const outcomesTotal = outcomeQuery.data?.total || 0;
  const scenariosTotal = scenarioQuery.data?.total || 0;
  const stepsTotal = stepQuery.data?.total || 0;
  const actionsTotal = actionQuery.data?.total || 0;
  const apisTotal = apiQuery.data?.total || 0;

  // Calculate pagination info
  const personasTotalPages = Math.ceil(personasTotal / 10);
  const outcomesTotalPages = Math.ceil(outcomesTotal / 10);
  const scenariosTotalPages = Math.ceil(scenariosTotal / 10);
  const stepsTotalPages = Math.ceil(stepsTotal / 10);
  const actionsTotalPages = Math.ceil(actionsTotal / 10);
  const apisTotalPages = Math.ceil(apisTotal / 10);

  return {
    // Data
    personas,
    outcomes,
    scenarios,
    steps,
    actions,
    apis,
    
    // Total counts
    personasTotal,
    outcomesTotal,
    scenariosTotal,
    stepsTotal,
    actionsTotal,
    apisTotal,
    
    // Page info
    personaPage,
    outcomePage,
    scenarioPage,
    stepPage,
    actionPage,
    apiPage,
    personasTotalPages,
    outcomesTotalPages,
    scenariosTotalPages,
    stepsTotalPages,
    actionsTotalPages,
    apisTotalPages,
    
    // Selected items
    selectedPersona,
    selectedOutcome,
    selectedScenario,
    selectedStep,
    selectedAction,
    
    // Handlers
    handlePersonaClick,
    handleOutcomeClick,
    handleScenarioClick,
    handleStepClick,
    handleActionClick,
    
    // Page navigation handlers
    handlePersonaNextPage,
    handlePersonaPrevPage,
    handleOutcomeNextPage,
    handleOutcomePrevPage,
    handleScenarioNextPage,
    handleScenarioPrevPage,
    handleStepNextPage,
    handleStepPrevPage,
    handleActionNextPage,
    handleActionPrevPage,
    handleApiNextPage,
    handleApiPrevPage,
    
    // Query objects for reference
    personaQuery,
    outcomeQuery,
    scenarioQuery,
    stepQuery,
    actionQuery,
    apiQuery,
    
    // Loading states
    isLoadingPersonas: personaQuery.isLoading,
    isLoadingOutcomes: outcomeQuery.isLoading,
    isLoadingScenarios: scenarioQuery.isLoading,
    isLoadingSteps: stepQuery.isLoading,
    isLoadingActions: actionQuery.isLoading,
    isLoadingApis: apiQuery.isLoading,

    // Search states
    outcomeSearch,
    setOutcomeSearch,
    scenarioSearch,
    setScenarioSearch,
    stepSearch,
    setStepSearch,
    actionSearch,
    setActionSearch,
    apiSearch,
    setApiSearch,
    projectUuid: id,
    setSelectedPersona,
    setSelectedOutcome,
    setSelectedScenario,
    setSelectedStep,
    setSelectedAction,
  };
};